'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Zone } from '@/types/zones/zone';
import { PRISMIC_ZONE_LAYOUT } from '@/app/constants/zone';
import { useGlobalContext } from '@/store/contexts/global-context';
import { useStore } from '@/store/store';
import type { Region } from '@/types/constants';
import type { SimilarGamesSlice } from '@/types/zones/slices/similar-games';
import {
    getSimilarGamesZone,
    MIN_SIMILAR_GAMES,
    parseSimilarGamesZone,
    type SimilarGamesZoneData
} from '@/logic/games/similar-games';

let cachedSimilarGamesZone: Zone | null = null;

type UseSimilarGamesZoneOptions = {
    initialZone: Zone;
    gridLayout?: PRISMIC_ZONE_LAYOUT;
};

export const useSimilarGamesZone = ({
    initialZone,
    gridLayout = PRISMIC_ZONE_LAYOUT.DEFAULT
}: UseSimilarGamesZoneOptions): SimilarGamesZoneData | null => {
    const { region, isMobile, isAndroidWrapper } = useGlobalContext();
    const recentGames = useStore((state) => state.userContext.recentGames);
    const [zone, setZone] = useState<Zone>(() => cachedSimilarGamesZone ?? initialZone);
    const channel = isAndroidWrapper ? 'android' : 'desktop';

    const zoneData = useMemo(
        () => parseSimilarGamesZone(zone, gridLayout, { region: region as Region, isMobile }, recentGames),
        [zone, gridLayout, region, isMobile, recentGames]
    );

    const fetchSimilarGamesZoneForAnchor = useCallback(
        async (anchorGameUid: string) => {
            if (!anchorGameUid) {
                return;
            }

            const similarGames = await getSimilarGamesZone(anchorGameUid, region as Region, channel);

            if (similarGames && (similarGames.data.body[0]?.items?.length ?? 0) >= MIN_SIMILAR_GAMES) {
                cachedSimilarGamesZone = similarGames;
                setZone(similarGames);
            }
        },
        [region, channel]
    );

    useEffect(() => {
        const anchorGameUid = recentGames[0] ?? '';
        const zoneAnchorGameUid = (zone.data.body[0] as SimilarGamesSlice)?.anchor_game_uid ?? '';

        if (!anchorGameUid || anchorGameUid === zoneAnchorGameUid) {
            return;
        }

        void fetchSimilarGamesZoneForAnchor(anchorGameUid);
    }, [recentGames, fetchSimilarGamesZoneForAnchor, zone.data.body]);

    if (zoneData.games.length < MIN_SIMILAR_GAMES) {
        return null;
    }

    return zoneData;
};
